// Laag 3 van het gezondheidsbord (B7) en het paneel Voorwaarden.
//
// Het meterpaneel: het getal met zijn stand, het verloop, de drie slechtste
// voorbeelden, de feiten uit de meterlijst en de stand bij alle proposities.
// Bevestigd (bij TR2) en Skills per trend (bij SK1) staan als extra regel onder
// "Ook gemeten": zo staat het in de meterlijst (`ook`), niet als eigen meter.
//
// Het paneel Voorwaarden: VW1–VW17 per propositie, en daaronder wat vroeger het
// hele vlak Gezondheid was: lanen, wachters, stille nul (GezondheidDraait.tsx).

import type { ReactNode } from "react";

import { PanelHeader, SectionLabel, Sparklijn } from "../../components/ui";
import type {
  GezondheidsbordCel,
  GezondheidsbordMeter,
  GezondheidsbordResponse,
} from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { Draait } from "./GezondheidDraait";
import { KLEUR, Pil, cijfer, getal, kort, maand } from "./GezondheidStukken";

type Sector = GezondheidsbordResponse["sectoren"][number];

const SOORT: Record<GezondheidsbordMeter["soort"], string> = {
  stuur: "stuurmeter: moet een kant op",
  balans: "balansmeter: moet binnen een band of onder een plafond blijven",
  voorwaarde: "voorwaarde: in orde of niet",
  signaal: "signaal: alleen ter informatie",
};

const HOE: Record<GezondheidsbordMeter["hoeGemeten"], string> = {
  automatisch: "automatisch, elke maandronde",
  alleen_bij_uiteenlopen: "twee modelfamilies; een derde alleen als ze uiteenlopen",
  eenmalige_ijking: "eenmalige ijking",
};

// minmax(0, 1fr): de kolom blijft zo breed als het paneel. Zonder die regel
// rekt de lanentabel (700 px, met een eigen schuifvak) op een telefoon het hele
// paneel op en schuift alles mee.
const binnen = {
  padding: "18px 24px 28px",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 22,
} as const;

export function MeterPaneel({
  meter,
  sector,
  bord,
  onClose,
}: {
  meter: GezondheidsbordMeter;
  /** De propositie waar je vandaan klikte; null vanuit Voorwaarden. */
  sector: Sector | null;
  bord: GezondheidsbordResponse;
  onClose: () => void;
}) {
  const vak = bord.vakjes.find((v) => v.code === meter.vak);
  const cel = sector ? meter.per[sector.sleutel] : undefined;
  const ict = bord.sectoren.find((s) => s.ondergrens);
  const ictCel = ict ? meter.per[ict.sleutel] : undefined;
  const naam = (code: string) => bord.meters.find((m) => m.code === code)?.naam ?? code;
  const pad = [sector?.naam, vak?.naam ?? "Voorwaarden", meter.code].filter(Boolean).join(" · ");

  return (
    <div data-paneel={meter.code}>
      <PanelHeader onClose={onClose}>
        <span style={{ fontSize: size.meta, color: color.faint }}>{pad}</span>
      </PanelHeader>
      <div style={binnen}>
        <h2
          style={{
            margin: 0,
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: color.ink,
          }}
        >
          {meter.naam}
        </h2>

        {sector && cel && !sector.ondergrens && (
          <Stand cel={cel} meter={meter} ict={ictCel?.waarde ?? null} />
        )}

        {sector && cel && (
          <section>
            <SectionLabel>Verloop</SectionLabel>
            <Verloop cel={cel} meter={meter} />
          </section>
        )}

        {sector && cel && (
          <section>
            <SectionLabel>Drie slechtste voorbeelden</SectionLabel>
            <Slechtste cel={cel} />
          </section>
        )}

        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "max-content minmax(0,1fr)",
            gap: "8px 18px",
            margin: 0,
            fontSize: size.meta,
            lineHeight: 1.5,
          }}
        >
          <Feit dt="Wat hij telt">{meter.meter}</Feit>
          <Feit dt="Waarom">{meter.waarom}</Feit>
          <Feit dt="Ook gemeten">{meter.ook}</Feit>
          <Feit dt="Soort">{SOORT[meter.soort]}</Feit>
          <Feit dt="Lat">{meter.lat}</Feit>
          <Feit dt="Hendel">{meter.hendel}</Feit>
          <Feit dt="Tegenmeter">
            {meter.tegenmeter.length
              ? meter.tegenmeter.map((c) => (
                  <div key={c}>{`${c} ${naam(c)}: mag niet slechter worden`}</div>
                ))
              : null}
          </Feit>
          <Feit dt="Hoe gemeten">
            {[HOE[meter.hoeGemeten], meter.ijkbron].filter(Boolean).join(" · ")}
          </Feit>
          <Feit dt="Herkomst">{meter.herkomst}</Feit>
          <Feit dt="Vervangt">{meter.vervangt.length ? meter.vervangt.join(", ") : null}</Feit>
        </dl>

        <section>
          <SectionLabel>Alle proposities</SectionLabel>
          <AlleProposities meter={meter} sectoren={bord.sectoren} />
        </section>
      </div>
    </div>
  );
}

function Feit({ dt, children }: { dt: string; children: ReactNode }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <>
      <dt style={{ color: color.faint }}>{dt}</dt>
      <dd style={{ margin: 0, color: color.body }}>{children}</dd>
    </>
  );
}

/** Het grote getal, de stand en waar het getal vandaan komt. */
function Stand({
  cel,
  meter,
  ict,
}: {
  cel: GezondheidsbordCel;
  meter: GezondheidsbordMeter;
  ict: number | null;
}) {
  const vorigeMaand = maand(cel.vorigePeriode);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <span
          style={{
            ...cijfer,
            fontSize: 30,
            fontWeight: weight.bold,
            color: color.ink,
            lineHeight: 1.1,
          }}
        >
          {getal(cel.waarde, meter.eenheid)}
        </span>
        <Pil kleur={cel.stand} title={cel.reden ?? undefined}>
          {cel.standWoord}
        </Pil>
        <span style={{ fontSize: size.meta, color: color.faint }}>
          {`ICT ${getal(ict, meter.eenheid)} · doel ${cel.doelTekst}`}
        </span>
      </div>
      {cel.waarde === null && cel.reden && (
        <p style={{ margin: 0, fontSize: size.meta, color: color.soft }}>{cel.reden}</p>
      )}
      {cel.doelReden && (
        <p style={{ margin: 0, fontSize: size.meta, color: color.soft }}>{cel.doelReden}</p>
      )}
      <p style={{ margin: 0, fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
        {cel.bron === "voorbeeld"
          ? "Uit de voorbeeldtelling in de meterlijst: er staat nog geen meting in de database."
          : cel.bron === "meting"
            ? [
                `Gemeten in ${maand(cel.periode) ?? "een onbekende maand"}.`,
                cel.vorige !== null
                  ? `Vorige meting${vorigeMaand ? ` (${vorigeMaand})` : ""}: ${getal(
                      cel.vorige,
                      meter.eenheid,
                    )}${cel.beter === true ? ", nu beter" : cel.beter === false ? ", nu slechter" : ""}.`
                  : "Nog geen vorige meting.",
              ].join(" ")
            : null}
      </p>
    </div>
  );
}

function Verloop({ cel, meter }: { cel: GezondheidsbordCel; meter: GezondheidsbordMeter }) {
  const punten = cel.verloop.filter((p) => p.waarde !== null);
  if (punten.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
        Het verloop begint bij de eerste meting in de database. Na twee maandrondes staat hier
        een lijn.
      </p>
    );
  }
  return (
    <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
      <Sparklijn waarden={punten.map((p) => p.waarde as number)} />
      <ol
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          display: "flex",
          gap: "4px 14px",
          flexWrap: "wrap",
          fontSize: size.meta,
          color: color.body,
        }}
      >
        {punten.map((p) => (
          <li key={p.periode} style={cijfer}>
            <span style={{ color: color.faint }}>{maand(p.periode)}</span>{" "}
            {getal(p.waarde, meter.eenheid)}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Slechtste({ cel }: { cel: GezondheidsbordCel }) {
  if (cel.slechtste.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
        {cel.bron === "meting"
          ? "Deze meting bewaarde geen voorbeelden."
          : "Komen mee met de eerste meting in de database."}
      </p>
    );
  }
  return (
    <ol style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 8, fontSize: size.meta }}>
      {cel.slechtste.map((v, i) => (
        <li key={v.id ?? `${i}-${v.titel}`} style={{ color: color.body, lineHeight: 1.5 }}>
          <span style={{ color: color.ink, fontWeight: weight.medium }}>{v.titel}</span>
          {v.waarom && <span style={{ color: color.soft }}>{` — ${v.waarom}`}</span>}
        </li>
      ))}
    </ol>
  );
}

function AlleProposities({
  meter,
  sectoren,
}: {
  meter: GezondheidsbordMeter;
  sectoren: readonly Sector[];
}) {
  // ICT bovenaan: dat is de ondergrens waar de rest tegen staat.
  const volgorde = [...sectoren.filter((s) => s.ondergrens), ...sectoren.filter((s) => !s.ondergrens)];
  return (
    <div style={{ display: "grid" }}>
      {volgorde.map((s, i) => {
        const c = meter.per[s.sleutel];
        return (
          <div
            key={s.sleutel}
            data-propositie={s.sleutel}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              padding: "8px 0",
              borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
              fontSize: size.meta,
            }}
          >
            <span style={{ color: color.ink, minWidth: 0 }}>
              {s.naam}
              {/* De reden zichtbaar, niet alleen in een title: die haal je op
                  een telefoon of met het toetsenbord niet op. */}
              {c?.reden && (
                <span style={{ display: "block", fontSize: 12, color: color.faint }}>{c.reden}</span>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span style={{ ...cijfer, color: color.body }} title={c?.reden ?? undefined}>
                {getal(c?.waarde, meter.eenheid)}
              </span>
              {c && (
                <Pil kleur={c.stand} title={c.reden ?? undefined}>
                  {c.standWoord}
                </Pil>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function VoorwaardenPaneel({
  bord,
  onClose,
  onKiesMeter,
}: {
  bord: GezondheidsbordResponse;
  onClose: () => void;
  onKiesMeter: (code: string) => void;
}) {
  const vw = bord.meters.filter((m) => m.vak === "voorwaarden");
  const { voorwaardenRood: rood, voorwaardenGeteld: geteld } = bord.strook;
  return (
    <div data-paneel="voorwaarden">
      <PanelHeader onClose={onClose}>
        <span style={{ fontSize: size.meta, color: color.faint }}>
          Voorwaarden · alle proposities
        </span>
      </PanelHeader>
      <div style={binnen}>
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: size.panelTitle,
              fontWeight: weight.bold,
              color: color.ink,
            }}
          >
            Klopt de basis?
          </h2>
          <p style={{ margin: "8px 0 0", fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
            De scores op het bord tellen pas als alles hieronder groen is.{" "}
            {geteld > 0
              ? `Nu ${rood} van de ${geteld} getelde voorwaarden niet in orde.`
              : "Er is nog geen voorwaarde geteld."}
          </p>
        </div>

        <div style={{ display: "grid" }}>
          {vw.map((m, i) => (
            <button
              key={m.code}
              type="button"
              data-meter={m.code}
              onClick={() => onKiesMeter(m.code)}
              style={{
                display: "grid",
                gap: 6,
                textAlign: "left",
                background: "none",
                border: "none",
                borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                padding: "11px 0",
                font: "inherit",
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: size.body, color: color.ink, fontWeight: weight.medium }}>
                <span style={{ ...cijfer, color: color.faint, fontSize: size.label }}>{m.code}</span>{" "}
                {m.naam}
                <span style={{ color: color.faint, fontWeight: weight.regular, fontSize: size.meta }}>
                  {` · ${m.doelTekst}`}
                </span>
              </span>
              <span style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.45 }}>
                {m.meter}
              </span>
              <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {bord.sectoren.map((s) => {
                  const c = m.per[s.sleutel];
                  return (
                    <Pil
                      key={s.sleutel}
                      kleur={s.ondergrens || !c ? "grijs" : c.stand}
                      title={c ? (c.reden ?? c.standWoord) : undefined}
                    >
                      {`${kort(s.naam)} ${getal(c?.waarde, m.eenheid)}`}
                    </Pil>
                  );
                })}
              </span>
            </button>
          ))}
        </div>

        <section
          style={{
            borderTop: `1px solid ${color.line}`,
            paddingTop: 20,
          }}
        >
          <h2
            style={{
              margin: "0 0 4px",
              fontSize: size.lead,
              fontWeight: weight.bold,
              color: color.ink,
            }}
          >
            Draait het?
          </h2>
          <p style={{ margin: "0 0 16px", fontSize: size.meta, color: color.soft }}>
            De lanen, de runs die aandacht vragen, de stille nul en de wachters.
          </p>
          <Draait />
        </section>
      </div>
    </div>
  );
}

/** Een gekleurd vlakje voor de samenvatting van laag 2. */
export function Zin({ kleur, children }: { kleur: keyof typeof KLEUR; children: ReactNode }) {
  return (
    <div
      style={{
        background: kleur === "grijs" ? color.surfaceQuote : KLEUR[kleur].vlak,
        color: color.body,
        borderRadius: radius.quote,
        padding: "10px 14px",
        fontSize: size.meta,
        lineHeight: 1.55,
      }}
    >
      {children}
    </div>
  );
}
